import baseConnectivity from '../common/base'
import { connCheckMethods } from '../../constants'
import { defaults } from 'lodash/fp'

// Parse and/or Validate
import { validation as v8n, parse, enums } from '../../../src/common/validation'

const defaultValues = {
  method: connCheckMethods.KEEP_ALIVE,
  pingInterval: 30000,
  reconnectLimit: 5,
  reconnectDelay: 5000,
  reconnectTimeMultiplier: 1,
  reconnectTimeLimit: 640000,
  autoReconnect: true,
  maxMissedPings: 3,
  checkConnectivity: true,
  webSocketOAuthMode: 'query'
}

// config validation
const v8nValidation = v8n.schema({
  method: enums([connCheckMethods.KEEP_ALIVE]),
  pingInterval: v8n.positive(),
  reconnectLimit: v8n.positive(),
  reconnectDelay: v8n.positive(),
  reconnectTimeMultiplier: v8n.positive(),
  reconnectTimeLimit: v8n.positive(),
  autoReconnect: v8n.boolean(),
  maxMissedPings: v8n.positive(),
  checkConnectivity: v8n.boolean(),
  webSocketOAuthMode: v8n.string()
})
const parseOptions = parse('connectivity', v8nValidation)

/**
 * Configuration options for the Connectivity feature.
 * The SDK can only use keepalive as the connectivity check.
 *
 * Keep Alive: The client sends "keepalive" messages (to the server) on the websocket at regular intervals. This lets the server know that the client is still connected, and that it should "keep the connection alive".
 *
 * For more information on keepalive see here: https://en.wikipedia.org/wiki/Keepalive
 * @public
 * @name config.connectivity
 * @memberof config
 * @instance
 * @param {Object} connectivity Connectivity configs.
 * @param {Number} [connectivity.pingInterval=30000] Time in between websocket ping attempts (milliseconds).
 * @param {Number} [connectivity.reconnectLimit=5] Number of failed reconnect attempts before reporting an error. Can be set to 0 to not limit reconnection attempts.
 * @param {Number} [connectivity.reconnectDelay=5000] Base time between websocket reconnect attempts (milliseconds).
 * @param {Number} [connectivity.reconnectTimeMultiplier=1] Reconnect delay multiplier for subsequent attempts. The reconnect delay time will be multiplied by this after each failed reconnect attempt to increase the delay between attempts. eg. 5000ms then 10000ms then 20000ms delay if value is 2.
 * @param {Number} [connectivity.reconnectTimeLimit=640000] Maximum time delay between reconnect attempts (milliseconds). Used in conjunction with the reconnect time multiplier to prevent overly long delays between reconnection attempts.
 * @param {Boolean} [connectivity.autoReconnect=true] Flag to determine whether the SDK will attempt to automatically reconnect after connectivity disruptions.
 * @param {Number} [connectivity.maxMissedPings=3] Maximum pings sent (without receiving a response) before reporting an error.
 * @param {Boolean} [connectivity.checkConnectivity=true] Flag to determine whether the SDK should check connectivity.
 * @param {string} [connectivity.webSocketOAuthMode=query] query will send the bearer access token to authenticate the websocket and none will not send it.
 */
export default function connectivity (options = {}) {
  options = defaults(defaultValues, options)
  parseOptions(options)
  return baseConnectivity(options)
}
